import { Avatar, Card, Center, Flex, Loader, Text, useMantineColorScheme } from "@mantine/core"

import classes from "@/pages/saveBookingPage.module.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { convertTo24HourFormat, secondsToTimeFormat } from "@/utils/availability";
import { IconArrowBack, IconArrowForward, IconCash, IconClockHour9 } from "@tabler/icons-react";
import { GoogleCalendarEvent, User } from "@/api/users";
import { useMediaQuery } from "@mantine/hooks";
import { AgendaConfiguration } from "@/api/agendaConfiguration";
import { LinkifyText } from "../LinkifyText/LinkifyText";
import { MyDayPicker } from "../MyDayPicker/MyDayPicker";
import { Booking } from "@/api/booking";

interface SaveBookingDateProps {
  loading: boolean;
  setStartDateToBooking: (date: Date) => void;
  nextStep: () => void;
  prevStep: () => void;
  localUser: User;
  agendaConfiguration: AgendaConfiguration;
  extension: string;
  hasPrevStep: boolean;
}

export const SaveBookingDate = (
  {
    loading,
    setStartDateToBooking,
    nextStep,
    prevStep,
    localUser,
    agendaConfiguration,
    extension,
    hasPrevStep
  }: SaveBookingDateProps
) => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [googleCalendarEvents, setGoogleCalendarEvents] = useState<GoogleCalendarEvent[]>([]);

  const isMobile = useMediaQuery('(max-width: 768px)');
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const prevSelectedDate = useRef(selectedDate);
  const [selectedHour, setSelectedHour] = useState<string | undefined>(undefined);
  const prevSelectedHour = useRef(selectedHour);

  const [dayPickerHeight, setDayPickerHeight] = useState<number | undefined>(undefined);
  const [dayPickerWidth, setDayPickerWidth] = useState<number | undefined>(undefined);
  const dayPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dayPickerRef.current) {
      setDayPickerHeight(dayPickerRef.current.offsetHeight);
      setDayPickerWidth(dayPickerRef.current.offsetWidth);
    }
    if (!selectedDate) {
      setSelectedHour(undefined);
    }
    if (selectedDate && selectedDate !== prevSelectedDate.current) {
      setSelectedHour(undefined);
      prevSelectedDate.current = selectedDate;
    }
  }, [selectedDate]);

  const setDateToBooking = useCallback(
    () => {
      if (!selectedDate) return;
      if (!selectedHour) return;
      const [startHours, startMinutes] = convertTo24HourFormat(selectedHour).split(':').map(Number);
      setStartDateToBooking(new Date(
        new Date(selectedDate).getFullYear(),
        new Date(selectedDate).getMonth(),
        new Date(selectedDate).getDate(),
        startHours,
        startMinutes,
        0
      ));
    }, [selectedHour, selectedDate]
  );

  useEffect(() => {
    if (selectedHour && selectedHour !== prevSelectedHour.current) {
      nextStep();
      setDateToBooking();
      prevSelectedHour.current = selectedHour;
    }
  }, [selectedHour]);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder className={classes.card}>
      {loading ? (
        <Center>
          <Loader></Loader>
        </Center>
      ) : (
        <>
          {hasPrevStep && (
            <IconArrowBack
              size={32}
              style={{ position: 'absolute', bottom: '1.25rem', left: '1.25rem' }}
              className={classes.arrowForward}
              onClick={() => {
                if (!loading) prevStep();
              }}
            />
          )}
          {selectedHour && (
            <IconArrowForward
              size={32}
              style={{ position: 'absolute', bottom: '1.25rem', right: '1.25rem' }}
              color="black"
              className={classes.arrowForward}
              onClick={() => nextStep()}
            />
          )}
          <div
            className={classes.sessionInfo}
            style={{
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
              borderRight: isMobile
                ? 'none'
                : `1px solid ${ !isDark ? 'var(--mantine-color-gray-4)' : 'var(--mantine-color-dark-4)'}`,
            }}
          >
            <Avatar
              src={localUser.picture}
              alt={localUser.name}
              size={150}
              style={{ marginBottom: '15px', objectFit: 'cover' }}
            />
            <Text style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{localUser.name}</Text>

            <Flex gap={"md"} align={"center"} justify={(agendaConfiguration?.showPrice === undefined || agendaConfiguration?.showPrice) ? 'space-between' : 'center'}>
              <Flex gap={"4"} align={"center"} >
                <IconClockHour9 size={18} />
                <Text style={{ fontWeight: 'bold', lineHeight: '1' }}>
                  {agendaConfiguration?.sessionDuration ? secondsToTimeFormat(agendaConfiguration?.sessionDuration) : "---"}
                </Text>
              </Flex>
              {(agendaConfiguration?.showPrice === undefined || agendaConfiguration?.showPrice) && (
                <Flex gap={"4"} align={"center"}>
                  <IconCash size={22} />
                  <Text style={{ fontWeight: 'bold', lineHeight: '1' }}>
                    {`$${agendaConfiguration?.paymentAmount ?? "---"} ${agendaConfiguration?.paymentCurrency ?? ""}`}
                  </Text>
                </Flex>
              )}
            </Flex>

            <Text
              style={{
                textAlign: 'left',
                width: isMobile ? '100%' : '60%',
                marginTop: '5px',
                whiteSpace: 'pre-line',
                wordBreak: 'break-word',
              }}
              size="sm"
            >
              <LinkifyText text={localUser?.description || ''} />
            </Text>
          </div>
          <div ref={dayPickerRef} className={classes.sessionCalendar} style={{position: "relative"}}>
            <MyDayPicker
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              agendaConfiguration={agendaConfiguration}
              bookings={bookings}
              setBookings={setBookings}
              extension={extension}
              googleCalendarEvents={googleCalendarEvents}
              setGoogleCalendarEvents={setGoogleCalendarEvents}
              selectedHour={selectedHour}
              setSelectedHour={setSelectedHour}
              startMonth={selectedDate}
              dayPickerHeight={
                dayPickerWidth && dayPickerHeight && dayPickerWidth > dayPickerHeight
                  ? dayPickerHeight
                  : undefined
              }
              localUser={localUser}
            />
          </div>
        </>
      )}
    </Card>
  )
}